import { type FC, type RefObject, useCallback, useEffect, useMemo } from 'react';

import { HotTable, type HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import { useTreatmentGridValidation } from '@/components/Modals/treatmentGrid/useTreatmentGridValidation';
import type { components } from '@/generated/internal-api/openapi-types';
import { registerHOTModules } from '@/support/hot';
import { TREATMENT_NAME_ERRORS, type TreatmentTypeEditRow } from '@/utils/treatmentTypeValidation';

import {
  buildAfterRendererCallback,
  buildCellsCallback,
  buildColumns,
  initBulkEditTreatmentsSettings,
} from './BulkEditTreatments.utils';

registerHOTModules();

type TreatmentTypeGlossary = components['schemas']['TreatmentTypeGlossaryV1.schema'];

interface HotTableRefLike {
  hotInstance?: unknown;
}

interface BulkEditTreatmentsTableProps {
  data: Array<TreatmentTypeEditRow>;
  innerRef: RefObject<HotTableRefLike | null>;
  treatments: Array<TreatmentTypeGlossary>;
  setInvalid: (invalid: boolean) => void;
}

/**
 * Spreadsheet grid for renaming treatment types in bulk. Each row carries its treatment's
 * `api_id` in a hidden column, so the save payload identifies treatments by identity rather
 * than by row position.
 */
const BulkEditTreatmentsTable: FC<BulkEditTreatmentsTableProps> = ({ data, innerRef, treatments, setInvalid }) => {
  const columns = useMemo(() => buildColumns({ treatments }), [treatments]);
  const settings = useMemo(() => initBulkEditTreatmentsSettings(columns), [columns]);
  const cellsCallback = useMemo(() => buildCellsCallback(treatments, data), [treatments, data]);
  const archivedTooltipRenderer = useMemo(() => buildAfterRendererCallback(treatments, data), [treatments, data]);

  const { afterValidate, afterChange, afterRemoveRow, afterUndoRedo, afterRenderer, revalidate } =
    useTreatmentGridValidation({
      hotRef: innerRef,
      setInvalid,
      fallbackMessage: TREATMENT_NAME_ERRORS.notValid,
    });

  useEffect(() => {
    revalidate();
  }, [revalidate, data]);

  /**
   * Both renderers stamp tooltip attributes on the same cell. The validation renderer runs first
   * so it can clear a stale error, then the archived renderer marks read-only rows — an archived
   * row is never editable, so the two can never contest the same cell.
   */
  const composedAfterRenderer = useCallback(
    (
      td: HTMLTableCellElement,
      row: number,
      col: number,
      prop: string | number,
      value: unknown,
      cellProperties: object
    ): void => {
      afterRenderer(td, row, col);
      archivedTooltipRenderer(td, row, col, prop, value, cellProperties);
    },
    [afterRenderer, archivedTooltipRenderer]
  );

  return (
    <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles" data-testid="treatments-spreadsheet">
      <div className="hotSpreadSheet mt3 spreadSheet" data-testid="bulk-edit-treatments__hot">
        <div className="ui-card spreedSheet" data-testid="bulk-edit-treatments__hot-table">
          <HotTable
            ref={innerRef as RefObject<HotTableRef>}
            data={data}
            licenseKey={AppConfig.handsOnTableLicence}
            headerClassName="custom-header"
            maxRows={data.length}
            fillHandle={false}
            cells={cellsCallback}
            afterValidate={afterValidate}
            afterChange={afterChange}
            afterRemoveRow={afterRemoveRow}
            afterUndo={afterUndoRedo}
            afterRedo={afterUndoRedo}
            afterRenderer={composedAfterRenderer}
            testId="bulk-edit-treatments__hot-table-inner"
            {...settings}
          />
        </div>
      </div>
    </div>
  );
};

export default BulkEditTreatmentsTable;
